import type { SalesDecision } from './salesAgent';
import { ACTIVATION_EXPENSE_RATIO, ACTIVATION_SALES_DAYS } from './activation';

export interface SalesTeamMember {
  id: string;
  name: string;
  share: number;
  active: boolean;
  role: 'lead' | 'seller';
  /** Optional immutable Cognito sub. A name or email never grants access. */
  userId?: string;
}
export interface SalesTeam { revision: number; members: SalesTeamMember[]; }
export const DEFAULT_SALES_TEAM: SalesTeam = {
  revision: 0,
  members: [
    { id: 'alex', name: 'Alex', share: 70, active: true, role: 'lead' },
    { id: 'pol', name: 'Pol', share: 30, active: true, role: 'lead' },
  ],
};
export type SalesTaskStatus = 'todo' | 'in_progress' | 'done' | 'superseded';
export interface SalesTask {
  id: string;
  tenantId: string;
  accountName: string;
  /** Current owner contact, populated on read rather than stored in task history. */
  phone?: string | null;
  assigneeId: string;
  action: SalesDecision['action'];
  title: string;
  priority: SalesDecision['priority'];
  status: SalesTaskStatus;
  blockedReason: string;
  decisionId: string;
  decisionAt: string;
  createdAt: string;
  updatedAt: string;
  revision: number;
  outcome: string;
  assignedBy: 'automatic' | 'manual';
}
export interface SalesWorkBoard {
  team: SalesTeam;
  tasks: SalesTask[];
  manager: boolean;
  memberId: string | null;
}
/** Largest deficit against the target share. Integer arithmetic gives 7/3 per 10 new tasks. */
export function nextSalesAssignee(members: SalesTeamMember[], counts: Record<string, number>): string | undefined {
  const eligible = members.filter(member => member.active && member.share > 0);
  const share = eligible.reduce((sum, member) => sum + member.share, 0);
  const total = eligible.reduce((sum, member) => sum + (counts[member.id] ?? 0), 0);
  return eligible.reduce<SalesTeamMember | undefined>((best, member) => {
    const score = (total + 1) * member.share - (counts[member.id] ?? 0) * share;
    const previous = best ? (total + 1) * best.share - (counts[best.id] ?? 0) * share : -Infinity;
    return score > previous ? member : best;
  }, undefined)?.id;
}

export const SALES_PROCESS = [
  { id: 'activation', title: 'Activar la cuenta', goal: 'Que el responsable entienda sus números por su cuenta.', steps: ['Respetar el objetivo elegido: costos o números.', `Ayudar a cargar ${ACTIVATION_SALES_DAYS} días seguidos de ventas reales y facturas del mismo periodo.`, 'Guiar a Resultados o Análisis. La consulta debe hacerla el responsable fuera del día de una sesión con Alex.'], exit: `Activación oficial registrada: ${ACTIVATION_SALES_DAYS} días de ventas y facturas de al menos el ${ACTIVATION_EXPENSE_RATIO * 100} % de esas ventas. El resultado sigue siendo provisional si faltan gastos o inventario.` },
  { id: 'conversion', title: 'Convertir a pago', goal: 'Que contrate después de comprobar el valor de Alexia.', steps: ['Revisar que la prueba sigue vigente y la cuenta ya está activada.', 'Resolver la duda concreta y explicar el plan que encaja.', 'Llevar a los planes en Configuración, con los precios y condiciones actuales.'], exit: 'Primer pago confirmado por Stripe. Una visita al checkout no cuenta como venta.' },
  { id: 'retention', title: 'Acompañar y retener', goal: 'Que siga obteniendo valor y tenga ayuda cuando la necesita.', steps: ['Acompañar a las cuentas de pago que todavía no se han activado.', 'Usar el reporte semanal para reforzar el valor.', 'Atender incidencias, impagos y bajas; pasar los problemas al equipo.'], exit: 'Continuidad de pago y uso útil. No atribuir inactividad sin una señal verificada.' },
] as const;

export interface SalesTaskGuide {
  title: string;
  summary: string;
  steps: string[];
  doneWhen: string;
  completedNote: string;
}
/** Instructions are for the teammate doing the task, not for the restaurant owner. */
export const SALES_TASK_GUIDES: Record<SalesDecision['action'], SalesTaskGuide> = {
  sales: {
    title: `Ayudar a cargar ${ACTIVATION_SALES_DAYS} días de ventas`,
    summary: 'Escribe al cliente y ayúdale a traer una semana de ventas a Alexia.',
    steps: ['Pregunta qué punto de venta utiliza y si puede exportar sus ventas.', `Ayúdale a conectar un punto de venta compatible o importar ${ACTIVATION_SALES_DAYS} días seguidos de ventas reales en Ingresos.`, 'Comprueba con el cliente que los días se han cargado. Si se atasca, anota qué ayuda necesita.'],
    doneWhen: 'El cliente ha cargado la semana de ventas, o has registrado por qué no va a continuar.',
    completedNote: 'El cliente ha cargado una semana completa de ventas.',
  },
  invoices: {
    title: 'Ayudar a cargar las facturas que faltan',
    summary: 'Ya tiene ventas. Ayúdale a agregar las facturas del mismo periodo.',
    steps: ['Escribe al cliente y pídele las facturas de gasto del periodo de sus ventas.', 'Guíale a Gastos para cargarlas o importarlas. Los albaranes no cuentan como facturas.', 'Revisa con él que están fechadas en el mismo periodo. Anota si hay algo que le impide completar los gastos.'],
    doneWhen: `Las facturas reales del periodo suman al menos el ${ACTIVATION_EXPENSE_RATIO * 100} % de las ventas, o has registrado un bloqueo con responsable y siguiente paso.`,
    completedNote: 'El cliente ha cargado las facturas reales del periodo de sus ventas.',
  },
  results: {
    title: 'Pedir al cliente que mire sus resultados',
    summary: 'Sus datos están preparados. Pídele que entre y revise sus números por su cuenta.',
    steps: ['Envíale el mensaje de abajo con el enlace a Resultados.', 'Pídele que seleccione el periodo indicado en el mensaje y mire los resultados por su cuenta, otro día distinto al de una sesión con Alex.', 'Pregúntale si ha podido verlos y qué duda tiene. Si faltan gastos o inventario, explícale que las cifras aún son provisionales.'],
    doneWhen: 'El cliente ha consultado sus resultados por su cuenta, o has registrado por qué no va a continuar.',
    completedNote: 'El cliente ha consultado sus resultados por su cuenta.',
  },
  costs: {
    title: 'Ayudar a costear su primer plato',
    summary: 'Acompáñale con un plato que conozca bien, sin intentar cargar toda la carta.',
    steps: ['Pregunta cuál es su plato estrella y qué ingredientes, cantidades y porciones lleva.', 'Guíale a la conversación con Alexia para crear el borrador de la receta.', 'Revisad los precios reales y guardad el plato en la carta con su precio de venta. Pregúntale si entiende su costo y margen.'],
    doneWhen: 'Tiene el primer plato en la carta con costo real y precio de venta, o has anotado el bloqueo y quién le ayuda.',
    completedNote: 'El cliente tiene su primer plato en la carta con costo real y precio de venta.',
  },
  subscribe: {
    title: 'Ayudar al cliente a elegir un plan',
    summary: 'Ya ha obtenido valor. Resuelve su duda y acompáñale hasta la contratación.',
    steps: ['Escríbele y pregunta si quiere continuar usando Alexia y qué duda le queda.', 'Explícale el plan que encaja con su restaurante y llévale a Planes en Configuración para ver las condiciones actuales.', 'Comprueba si ha terminado el pago. Si todavía lo está pensando, guarda la respuesta y deja la tarea en seguimiento.'],
    doneWhen: 'El primer pago está confirmado, o el cliente ha dicho que no continúa y has anotado el motivo.',
    completedNote: 'El primer pago del cliente está confirmado.',
  },
  payment: {
    title: 'Revisar un pago pendiente con el cliente',
    summary: 'Comprueba los avisos de cobro antes de agregar otro contacto.',
    steps: ['Revisa si ya recibió un aviso de impago y si está en plazo para actualizar su tarjeta.', 'Si necesita ayuda, acompáñale al enlace de actualización del método de pago del aviso existente.', 'Comprueba si el cobro se ha recuperado. Si no, anota el problema y mantenlo en seguimiento.'],
    doneWhen: 'El pago se ha recuperado, o has registrado el problema y la persona que lo resolverá.',
    completedNote: 'El pago pendiente se ha recuperado.',
  },
  cancellation: {
    title: 'Hablar con el cliente sobre su baja',
    summary: 'Entiende qué le falta y ofrece una solución concreta una vez.',
    steps: ['Lee el motivo de baja que ya dejó antes de volver a preguntárselo.', 'Habla con él para entender el problema y ofrece una solución si podéis resolverlo.', 'Si mantiene la baja, ayúdale a completarla y registra el motivo. Si quiere seguir, anota qué habéis acordado.'],
    doneWhen: 'La decisión del cliente está clara y has registrado el motivo y lo acordado.',
    completedNote: 'He hablado con el cliente y registrado su decisión y lo acordado.',
  },
  extension: {
    title: 'Revisar si podemos darle siete días más',
    summary: 'Usa la oferta de prueba existente y ayúdale a completar el paso pendiente.',
    steps: ['Comprueba la elegibilidad en la sección de Extensión de prueba de la ficha del restaurante.', 'Si la oferta está disponible, prepárala desde esa sección: genera el enlace correcto y vuelve a comprobar los requisitos.', 'Cuando la acepte, ayúdale a completar el paso que le faltaba. Si la oferta no está disponible, registra el motivo.'],
    doneWhen: 'La oferta se ha gestionado y has registrado si fue aceptada o por qué no procede.',
    completedNote: 'He gestionado la oferta de prueba y registrado su resultado.',
  },
  handoff: {
    title: 'Resolver lo que necesita el cliente',
    summary: 'Lee el contexto, habla con el cliente y acuerda quién le ayuda.',
    steps: ['Lee la nota de seguimiento y el motivo por el que la cuenta necesita atención humana.', 'Contacta con el cliente para resolver la duda o acuerda qué persona del equipo se encarga.', 'Registra lo que habéis resuelto y el siguiente paso. Si sigue pendiente, déjala en seguimiento.'],
    doneWhen: 'El problema se ha resuelto, o has registrado una derivación con responsable y siguiente paso.',
    completedNote: 'He resuelto la consulta o registrado una derivación con responsable y siguiente paso.',
  },
  wait: {
    title: 'Esperar antes de volver a contactar',
    summary: 'Revisa el motivo de espera antes de hacer otra intervención.',
    steps: ['Lee el motivo de espera: puede haber un contacto reciente o una preferencia que impide escribir.', 'Si ya estáis hablando con el cliente, guarda lo que habéis acordado.', 'Vuelve a revisar la cuenta cuando haya una respuesta o cambie su situación.'],
    doneWhen: 'Hay una respuesta o un cambio que permite decidir el siguiente paso.',
    completedNote: 'He revisado el cambio de situación y registrado el siguiente paso.',
  },
};
